import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  AppBar,
  Toolbar,
  IconButton,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Tabs,
  Tab,
  Alert,
  Popover,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Save,
  ArrowBack,
  Send,
  Delete,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatListBulleted,
  FormatListNumbered,
  Link as LinkIcon,
  FormatColorText,
  SmartButton,
  DataObject,
  TextFields,
  AlignHorizontalLeft,
  AlignHorizontalCenter,
} from "@mui/icons-material";
import RedoIcon from "@mui/icons-material/Redo";
import UndoIcon from "@mui/icons-material/Undo";
import { showSuccess, showError } from "../utils/toast";
import { colors } from "../theme/modernTheme";
import RichTextEditor from "../components/editor/RichTextEditor";
import { emailTemplatesAPI, campaignsAPI } from "../services/api";
import { useLoading } from "../context/LoadingContext";
import DeleteConfirmationDialog from "../components/DeleteConfirmationDialog";

/**
 * EmailTemplateEditor Component
 * Full-page editor for creating and editing email templates
 * Validates: Requirements 1.1, 1.2, 7.2
 */
const EmailTemplateEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showLoading, hideLoading } = useLoading();
  const isEditMode = Boolean(id);

  // Get campaign context if coming from campaign page
  const campaignContext = location.state?.campaignData;
  const returnTo = location.state?.returnTo;
  const fromCampaign = location.state?.fromCampaign; // true if coming from campaign
  const selectedTags = location.state?.selectedTags || [];

  // Template state
  const [template, setTemplate] = useState({
    name: "",
    subject: "",
    html_body:
      "<p style='padding: 0 12px;'>Start typing your email content here...</p>",

    tags: [],
    is_default: false,
    notes: "",
    can_delete: true,
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0: Design, 1: HTML - Start with Design
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [tagInput, setTagInput] = useState("");
  const [testSendDialogOpen, setTestSendDialogOpen] = useState(false);
  const [testSendEmail, setTestSendEmail] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [textColorAnchor, setTextColorAnchor] = useState(null);
  const [bgColorAnchor, setBgColorAnchor] = useState(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const [buttonDialogOpen, setButtonDialogOpen] = useState(false);
  const [buttonUrl, setButtonUrl] = useState("");
  const [buttonText, setButtonText] = useState("Click Here");
  const [buttonColor, setButtonColor] = useState("#28a745");
  const [fontSizeMenuAnchor, setFontSizeMenuAnchor] = useState(null);
  const [placeholderMenuAnchor, setPlaceholderMenuAnchor] = useState(null);
  const [templateAlignment, setTemplateAlignment] = useState("left"); // New state for template alignment

  const editorRef = useRef(null);
  const previewIframeRef = useRef(null);
  const previousTabRef = useRef(0); // Track previous tab

  // Hard-coded dummy data for preview - Indian Jain person data
  const dummyData = {
    contact: {
      first_name: "Adinath",
      last_name: "Kothari",
      email: "adinath.kothari@example.com",
      phone: "+91 98765 43210",
      address: "Walkeshwar Road, Malabar Hill, Mumbai 400006",
      city: "Mumbai",
      state: "Maharashtra",
      country: "India",
    },
    // donation: {
    //   amount: "₹10,000",
    //   date: "02 December 2024",
    //   currency: "INR",
    //   payment_method: "UPI",
    //   frequency: "One-time",
    //   purpose: "General Donation",
    //   receipt_number: "RCP-2024-0123",
    //   transaction_id: "TXN20241202001",
    // },
    // campaign: {
    //   name: "Annual Fundraiser 2024",
    //   description: "Supporting community welfare programs",
    //   amount_raised: "₹3,50,000",
    // },
    // org: {
    //   name: "Jain Sangh Mumbai",
    //   email: "info@jainsanghmumbai.org",
    //   phone: "+91 22 2363 3627",
    //   address: "Walkeshwar Road, Malabar Hill, Mumbai, Maharashtra 400006",
    // },
  };

  // Available placeholders
  const availablePlaceholders = [
    // Contact placeholders
    {
      token: "{{contact.first_name}}",
      label: "Contact: First Name",
      group: "Contact",
    },
    {
      token: "{{contact.last_name}}",
      label: "Contact: Last Name",
      group: "Contact",
    },
    { token: "{{contact.email}}", label: "Contact: Email", group: "Contact" },
    { token: "{{contact.phone}}", label: "Contact: Phone", group: "Contact" },
    {
      token: "{{contact.address}}",
      label: "Contact: Address",
      group: "Contact",
    },
    { token: "{{contact.city}}", label: "Contact: City", group: "Contact" },
    { token: "{{contact.state}}", label: "Contact: State", group: "Contact" },
    {
      token: "{{contact.country}}",
      label: "Contact: Country",
      group: "Contact",
    },
    {
      token: "{{unsubscribe_link}}",
      label: "Unsubscribe Email",
      group: "Campaign",
    },

    // // Donation placeholders
    // {
    //   token: "{{donation.amount}}",
    //   label: "Donation: Amount",
    //   group: "Donation",
    // },
    // { token: "{{donation.date}}", label: "Donation: Date", group: "Donation" },
    // {
    //   token: "{{donation.currency}}",
    //   label: "Donation: Currency",
    //   group: "Donation",
    // },
    // {
    //   token: "{{donation.payment_method}}",
    //   label: "Donation: Payment Method",
    //   group: "Donation",
    // },
    // {
    //   token: "{{donation.frequency}}",
    //   label: "Donation: Frequency",
    //   group: "Donation",
    // },
    // {
    //   token: "{{donation.purpose}}",
    //   label: "Donation: Purpose",
    //   group: "Donation",
    // },
    // {
    //   token: "{{donation.receipt_number}}",
    //   label: "Donation: Receipt Number",
    //   group: "Donation",
    // },
    // {
    //   token: "{{donation.transaction_id}}",
    //   label: "Donation: Transaction ID",
    //   group: "Donation",
    // },

    // // Campaign placeholders
    // { token: "{{campaign.name}}", label: "Campaign: Name", group: "Campaign" },
    // {
    //   token: "{{campaign.description}}",
    //   label: "Campaign: Description",
    //   group: "Campaign",
    // },
    // {
    //   token: "{{campaign.amount_raised}}",
    //   label: "Campaign: Amount Raised",
    //   group: "Campaign",
    // },

    // // Organization placeholders
    // {
    //   token: "{{org.name}}",
    //   label: "Organization: Name",
    //   group: "Organization",
    // },
    // {
    //   token: "{{org.email}}",
    //   label: "Organization: Email",
    //   group: "Organization",
    // },
    // {
    //   token: "{{org.phone}}",
    //   label: "Organization: Phone",
    //   group: "Organization",
    // },
    // {
    //   token: "{{org.address}}",
    //   label: "Organization: Address",
    //   group: "Organization",
    // },
  ];

  // Load template if in edit mode
  useEffect(() => {
    if (isEditMode) {
      loadTemplate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Update preview when template content changes and preview tab is active
  useEffect(() => {
    if (activeTab === 1 && !id) {
      // Only update preview for unsaved templates when on preview tab
      let html = template.html_body || "<p>No content yet</p>";

      // Replace placeholder spans with dummy data
      // The editor stores placeholders as: <span data-placeholder="true" data-token="{{contact.first_name}}" ...>Label</span>
      Object.keys(dummyData).forEach((scope) => {
        Object.keys(dummyData[scope]).forEach((field) => {
          const token = `{{${scope}.${field}}}`;
          const value = dummyData[scope][field];

          // Escape special regex characters in the token
          const escapedToken = token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

          // Replace placeholder spans that have this token
          const spanRegex = new RegExp(
            `<span[^>]*data-token="${escapedToken}"[^>]*>.*?</span>`,
            "g",
          );
          html = html.replace(spanRegex, value);

          // Also replace plain tokens (in case they exist)
          html = html.replace(new RegExp(escapedToken, "g"), value);
        });
      });

      setPreviewHtml(html);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.html_body, activeTab]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+S or Cmd+S to save
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        handleSave();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, fieldErrors]);

  // Update iframe body alignment when templateAlignment changes
  useEffect(() => {
    if (previewIframeRef.current && activeTab === 0) {
      try {
        const iframeDoc =
          previewIframeRef.current.contentDocument ||
          previewIframeRef.current.contentWindow.document;
        if (iframeDoc.body) {
          iframeDoc.body.style.justifyContent =
            templateAlignment === "center" ? "center" : "flex-start";
        }
      } catch (error) {
        console.error("Error updating template alignment:", error);
      }
    }
  }, [templateAlignment, activeTab]);

  const loadTemplate = async () => {
    setLoading(true);
    try {
      const response = await emailTemplatesAPI.getById(id);
      const templateData = response.data || response;
      setTemplate(templateData);

      // Try to detect alignment from existing HTML structure
      if (templateData.html_body) {
        // Check if the HTML has center alignment in the table structure
        const hasCenterAlign = /align=["']center["']/i.test(
          templateData.html_body,
        );
        setTemplateAlignment(hasCenterAlign ? "center" : "left");
      }
    } catch (error) {
      showError("Failed to load template");
      navigate("/email-templates");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/email-templates");
  };

  const handleFieldChange = (field, value) => {
    // If changing html_body, preserve head content when it's a full HTML document
    if (field === "html_body" && value) {
      const trimmedValue = value.trim().toLowerCase();
      if (
        trimmedValue.startsWith("<!doctype") ||
        trimmedValue.startsWith("<html")
      ) {
        // Don't extract - keep the full HTML document as-is
        // The generateAlignedHtml function will handle extraction when saving
      }
    }

    setTemplate((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!template.name || !template.name.trim()) {
      newErrors.name = "Template name is required";
    }

    if (!template.subject || !template.subject.trim()) {
      newErrors.subject = "Subject line is required";
    }

    if (!template.html_body || !template.html_body.trim()) {
      newErrors.html_body = "Template content is required";
    }

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Replace any unsubscribe-related HTML content with {{unsubscribe_link}} placeholder
   * This function searches for links, buttons, or text containing unsubscribe-related terms
   * and replaces them with the placeholder that will be processed by the backend
   */
  const replaceUnsubscribeWithPlaceholder = (html) => {
    if (!html) return html;

    // Replace anchor tags containing unsubscribe-related content
    // Matches: <a href="...">...unsubscribe...</a>
    html = html.replace(
      /<a\s+[^>]*href=["'][^"']*["'][^>]*>([^<]*\b(?:unsubscribe|opt[\s-]?out|remove[\s-]?me|stop[\s-]?emails|email[\s-]?preferences)\b[^<]*)<\/a>/gi,
      "{{unsubscribe_link}}",
    );

    // Replace button elements containing unsubscribe-related content
    // Matches: <button ...>...unsubscribe...</button>
    html = html.replace(
      /<button\s+[^>]*>([^<]*\b(?:unsubscribe|opt[\s-]?out|remove[\s-]?me|stop[\s-]?emails|email[\s-]?preferences)\b[^<]*)<\/button>/gi,
      "{{unsubscribe_link}}",
    );

    // Replace any remaining standalone unsubscribe links (href containing unsubscribe)
    // Matches: <a href="...unsubscribe...">...</a>
    html = html.replace(
      /<a\s+[^>]*href=["'][^"']*\b(?:unsubscribe|opt[\s-]?out|remove|stop|preferences)\b[^"']*["'][^>]*>[^<]*<\/a>/gi,
      "{{unsubscribe_link}}",
    );

    // Replace paragraph or div elements where unsubscribe is the main action/intent
    // Only matches short text (< 100 chars) that's primarily about unsubscribing
    // This avoids matching long paragraphs that merely mention unsubscribe
    html = html.replace(
      /<(p|div)(\s+[^>]*)?>([^<]{0,100}?\b(?:unsubscribe|opt[\s-]?out|remove[\s-]?me|stop[\s-]?emails|email[\s-]?preferences)\b[^<]{0,20}?)<\/\1>/gi,
      (match, tag, attrs, content) => {
        // Additional check: don't replace if it contains "no unsubscribe" or similar negations
        if (
          /\b(?:no|not|without|never)\s+(?:unsubscribe|opt[\s-]?out)/i.test(
            content,
          )
        ) {
          return match;
        }
        return "{{unsubscribe_link}}";
      },
    );

    return html;
  };

  /**
   * Generate email-ready HTML with alignment wrapper baked in
   * This creates proper table-based layout for email clients
   * Extracts background color from content and applies to wrapper
   * Preserves head section for fonts and stylesheets
   */
  const generateAlignedHtml = (content, alignment, headContent = "") => {
    if (!content) return "";

    // Replace unsubscribe-related content with placeholder BEFORE processing
    content = replaceUnsubscribeWithPlaceholder(content);

    // Clean up the content
    let cleanContent = content.replace(/<title[^>]*>.*?<\/title>/gi, "");
    let extractedHead = headContent;

    // Check if content is already a full HTML document - extract head and body content
    if (
      cleanContent.trim().toLowerCase().startsWith("<!doctype") ||
      cleanContent.trim().toLowerCase().startsWith("<html")
    ) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(cleanContent, "text/html");

      // Extract head content (excluding title)
      if (doc.head && !extractedHead) {
        let headHtml = doc.head.innerHTML;
        // Remove title tags
        headHtml = headHtml.replace(/<title[^>]*>.*?<\/title>/gi, "");
        extractedHead = headHtml.trim();
      }

      cleanContent = doc.body ? doc.body.innerHTML : cleanContent;
    }

    // Extract background color from content
    let bgColor = "transparent";

    // Try to find background-color in style attribute
    const bgStyleMatch = cleanContent.match(/background-color:\s*([^;"\s>]+)/i);
    if (bgStyleMatch) {
      bgColor = bgStyleMatch[1];
    }

    // Try to find bgcolor attribute
    const bgAttrMatch = cleanContent.match(/bgcolor=["']([^"']+)["']/i);
    if (bgAttrMatch) {
      bgColor = bgAttrMatch[1];
    }

    // Generate table-based layout for email compatibility
    const alignAttr = alignment === "center" ? "center" : "left";
    const marginStyle = alignment === "center" ? "margin: 0 auto;" : "";
    const bgStyle =
      bgColor !== "transparent" ? `background-color: ${bgColor};` : "";
    const bgAttr = bgColor !== "transparent" ? `bgcolor="${bgColor}"` : "";

    // Build head section
    const headSection = extractedHead
      ? `<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  ${extractedHead}
</head>`
      : `<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>`;

    return `<!DOCTYPE html>
<html>
${headSection}
<body style="margin: 0; padding: 0; ${bgStyle}" ${bgAttr}>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width: 100%; margin: 0; padding: 0; ${bgStyle}" ${bgAttr}>
  <tr>
    <td align="${alignAttr}" valign="top" style="padding: 0; ${bgStyle}" ${bgAttr}>
      <table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" align="${alignAttr}" style="width: 600px; max-width: 600px; ${marginStyle}">
        <tr>
          <td style="font-family: Arial, sans-serif;">
            ${cleanContent}
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>
</body>
</html>`;
  };

  const handleSave = async (saveOption = null) => {
    // If on Design tab (index 0), capture the latest content before saving
    if (activeTab === 0 && previewIframeRef.current) {
      try {
        const iframeDoc =
          previewIframeRef.current.contentDocument ||
          previewIframeRef.current.contentWindow.document;

        // Get the content from inside the email-container div (not the wrapper itself)
        const emailContainer = iframeDoc.querySelector(".email-container");
        let bodyContent = emailContainer
          ? emailContainer.innerHTML
          : iframeDoc.body
            ? iframeDoc.body.innerHTML
            : "";

        // Remove any <title> tags that might have been added
        bodyContent = bodyContent.replace(/<title[^>]*>.*?<\/title>/gi, "");

        // Generate aligned HTML with table wrapper
        template.html_body = generateAlignedHtml(
          bodyContent,
          templateAlignment,
        );
      } catch (error) {
        console.error("Error capturing design content before save:", error);
      }
    }

    // If on HTML tab (index 1), wrap with alignment if not already wrapped
    if (activeTab === 1) {
      let htmlContent = template.html_body;

      // Check if it's a full HTML document - extract body content
      if (
        htmlContent.trim().toLowerCase().startsWith("<!doctype") ||
        htmlContent.trim().toLowerCase().startsWith("<html")
      ) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, "text/html");
        htmlContent = doc.body ? doc.body.innerHTML : htmlContent;
      }

      // Generate aligned HTML with table wrapper
      template.html_body = generateAlignedHtml(htmlContent, templateAlignment);
    }

    // Validate form
    if (!validateForm()) {
      // Don't show toast - field errors are displayed below each field
      return;
    }

    try {
      showLoading();
      let templateId;

      // Create template data - alignment is already baked into html_body
      const templateData = {
        ...template,
      };

      if (isEditMode) {
        // Update existing template
        await emailTemplatesAPI.update(id, templateData);
        templateId = id;
        showSuccess("Template updated successfully");
        navigate(`/email-templates`);
      } else {
        // Create new template
        const response = await emailTemplatesAPI.create(templateData);
        templateId = response.data?.id || response.id;
        showSuccess("Template created successfully");
        navigate(`/email-templates`);
      }

      // Handle campaign save if coming from campaign
      if (fromCampaign && campaignContext && templateId && saveOption) {
        try {
          let campaignStatus = "DRAFT";
          let successMessage = "Template saved successfully";

          // Determine campaign status based on save option
          if (saveOption === "activate") {
            campaignStatus = "ACTIVE";
            successMessage = "Campaign activated successfully!";
          } else if (saveOption === "draft") {
            campaignStatus = "DRAFT";
            successMessage = "Campaign saved as draft!";
          }

          const campaignData = {
            ...campaignContext,
            status: campaignStatus,
            email_template_id: templateId,
          };

          // Check if it's a new campaign or existing one
          const campaignIdMatch = returnTo?.match(/\/campaigns\/(.+)/);
          const campaignId = campaignIdMatch?.[1];

          let finalCampaignId;

          if (campaignId && campaignId !== "new") {
            // Update existing campaign
            await campaignsAPI.update(campaignId, campaignData);
            finalCampaignId = campaignId;

            // For updates, still need to assign tags separately
            if (selectedTags.length > 0) {
              for (const tag of selectedTags) {
                try {
                  await campaignsAPI.assignTag(finalCampaignId, {
                    tag_id: tag.id,
                  });
                } catch (error) {}
              }
            }
          } else {
            // Create new campaign
            const campaignResponse = await campaignsAPI.create(campaignData);
            finalCampaignId = campaignResponse.data.id;

            // Assign tags after campaign creation
            if (selectedTags.length > 0) {
              for (const tag of selectedTags) {
                try {
                  await campaignsAPI.assignTag(finalCampaignId, {
                    tag_id: tag.id,
                  });
                } catch (error) {}
              }
            }
          }

          showSuccess(successMessage);
          // Navigate to the campaign detail page
          navigate(`/campaigns/${finalCampaignId}`);
          return;
        } catch (campaignError) {
          showError("Template saved but failed to save campaign");
        }
      }

      // Navigate back or to edit mode
      if (returnTo && !fromCampaign) {
        navigate(returnTo, {
          state: {
            selectedTemplateId: templateId,
            campaignData: campaignContext,
          },
        });
      } else if (templateId && !isEditMode) {
        navigate(`/email-templates/${templateId}`, { replace: true });
      }
    } catch (error) {
      console.log("Error caught:", error); // Debug log

      // The API interceptor already unwraps error.response.data, so error is the data object
      if (error.error?.code === "VALIDATION_ERROR") {
        const errors = error.error.details?.field_errors || {};
        setFieldErrors(errors);
        // Don't show toast - field errors are displayed below each field
      } else {
        const errorMessage =
          error.error?.message || error.message || "Failed to save template";

        // Handle specific errors
        if (
          errorMessage.includes("unique") ||
          errorMessage.includes("already exists")
        ) {
          setFieldErrors({ name: "A template with this name already exists" });
          // Don't show toast - field error is displayed below name field
        } else {
          showError(errorMessage);
        }
      }
    } finally {
      hideLoading();
    }
  };

  const handleTestSendOpen = async () => {
    // Validate form first
    if (!validateForm()) {
      // Don't show toast - field errors are displayed below each field
      return;
    }

    // If on Design tab (index 0), capture the latest content before saving
    if (activeTab === 0 && previewIframeRef.current) {
      try {
        const iframeDoc =
          previewIframeRef.current.contentDocument ||
          previewIframeRef.current.contentWindow.document;

        // Get the content from inside the email-container div (not the wrapper itself)
        const emailContainer = iframeDoc.querySelector(".email-container");
        let bodyContent = emailContainer
          ? emailContainer.innerHTML
          : iframeDoc.body
            ? iframeDoc.body.innerHTML
            : "";

        // Remove any <title> tags that might have been added
        bodyContent = bodyContent.replace(/<title[^>]*>.*?<\/title>/gi, "");

        // Generate aligned HTML with table wrapper
        template.html_body = generateAlignedHtml(
          bodyContent,
          templateAlignment,
        );
      } catch (error) {
        console.error("Error capturing design content before save:", error);
      }
    }

    try {
      showLoading();
      let templateId = id;

      // Create template data - alignment is already baked into html_body
      const templateData = {
        ...template,
      };

      // Save the template first
      if (isEditMode) {
        // Update existing template
        await emailTemplatesAPI.update(id, templateData);
        showSuccess("Template saved successfully");
      } else {
        // Create new template
        const response = await emailTemplatesAPI.create(templateData);
        templateId = response.data?.id || response.id;
        showSuccess("Template created successfully");

        // Navigate to edit mode with the new template ID
        navigate(`/email-templates/${templateId}`, { replace: true });
      }

      // Open test send dialog after successful save
      setTestSendDialogOpen(true);
    } catch (error) {
      if (error.error?.code === "VALIDATION_ERROR") {
        const errors = error.error.details?.field_errors || {};
        setFieldErrors(errors);
        // Don't show toast - field errors are displayed below each field
      } else {
        const errorMessage =
          error.error?.message || error.message || "Failed to save template";
        showError(errorMessage);
      }
    } finally {
      hideLoading();
    }
  };

  const handleTestSendClose = () => {
    setTestSendDialogOpen(false);
    setTestSendEmail("");
  };

  const handleTestSend = async () => {
    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!testSendEmail || !emailRegex.test(testSendEmail)) {
      showError("Please enter a valid email address");
      return;
    }

    setTestSending(true);
    try {
      // Send test email - backend will use dummy data automatically
      await emailTemplatesAPI.testSend(id, testSendEmail);
      showSuccess(`Test email sent to ${testSendEmail} with dummy data`);
      handleTestSendClose();
    } catch (error) {
      const errorMessage =
        error.message || error.error?.message || "Failed to send test email";
      showError(errorMessage);
    } finally {
      setTestSending(false);
    }
  };

  // Handle tab change and load preview (Bug #9)
  const handleTabChange = async (event, newValue) => {
    const previousTab = previousTabRef.current;

    // When switching FROM Design tab (now index 0), save the content
    if (previousTab === 0 && previewIframeRef.current) {
      try {
        const iframeDoc =
          previewIframeRef.current.contentDocument ||
          previewIframeRef.current.contentWindow.document;
        iframeDoc.designMode = "off";

        // Get the content from inside the email-container div (not the wrapper itself)
        const emailContainer = iframeDoc.querySelector(".email-container");
        let bodyContent = emailContainer
          ? emailContainer.innerHTML
          : iframeDoc.body
            ? iframeDoc.body.innerHTML
            : "";

        // Remove any <title> tags that might have been added
        bodyContent = bodyContent.replace(/<title[^>]*>.*?<\/title>/gi, "");

        handleFieldChange("html_body", bodyContent);
      } catch (error) {
        console.error("Error saving design content:", error);
      }
    }

    setActiveTab(newValue);
    previousTabRef.current = newValue;

    // No preview loading needed anymore since we only have Design and HTML tabs
  };

  const handleDeleteOpen = () => {
    if (template.can_delete === false) {
      showError(
        "Cannot delete this template as it is currently being used in active campaigns.",
      );
      return;
    }
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      showLoading();
      // Soft delete by default
      await emailTemplatesAPI.delete(id);
      showSuccess("Template deleted successfully");
      navigate("/email-templates");
    } catch (error) {
      const errorMessage =
        error.message || error.error?.message || "Failed to delete template";
      showError(errorMessage);
    } finally {
      hideLoading();
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: colors.background.default,
        }}>
        <CircularProgress
          size={60}
          thickness={4}
          sx={{ color: colors.primary.main, mb: 3 }}
        />
        <Typography
          variant="h6"
          sx={{ color: colors.text.secondary, fontWeight: 500 }}>
          Loading template...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: colors.background.default,
      }}>
      {/* ARIA Live Region for Error Announcements */}
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        style={{
          position: "absolute",
          left: "-10000px",
          width: "1px",
          height: "1px",
          overflow: "hidden",
        }}>
        {Object.values(fieldErrors).filter(Boolean).join(". ")}
      </div>

      {/* Campaign Context Banner */}
      {campaignContext && fromCampaign && (
        <Box
          sx={{
            backgroundColor: colors.action.hover,
            borderBottom: `1px solid ${colors.action.selected}`,
            px: 3,
            py: 1.5,
          }}>
          <Typography
            variant="body2"
            sx={{
              color: colors.primary.dark,
              fontWeight: 500,
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}>
            Creating email template for campaign:{" "}
            <strong>{campaignContext.name || "New Campaign"}</strong>
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: colors.primary.dark, mt: 0.5, display: "block" }}>
            Template will be saved as draft and you'll be redirected to the
            campaign
          </Typography>
        </Box>
      )}

      {/* Top Bar */}
      <AppBar
        position="static"
        color="default"
        elevation={0}
        sx={{
          backgroundColor: "#FFFFFF",
          borderBottom: "1px solid #E5E7EB",
        }}>
        <Toolbar sx={{ py: 1 }}>
          <IconButton
            edge="start"
            onClick={handleBack}
            sx={{
              mr: 2,
              color: colors.primary.main,
              "&:hover": {
                backgroundColor: "rgba(90, 106, 207, 0.08)",
              },
            }}
            aria-label="back to templates">
            <ArrowBack />
          </IconButton>

          <Typography
            variant="h6"
            sx={{
              textTransform: "capitalize",
              flexGrow: 1,
              fontWeight: 700,
              color: "#000000",
              fontFamily: '"Poppins", sans-serif',
            }}
            component="h1">
            {isEditMode ? "Edit Email Template" : "Create Email Template"}
          </Typography>

          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<Send />}
              onClick={handleTestSendOpen}
              aria-label="test send"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderColor: "#E5E7EB",
                color: colors.text.secondary,
                borderRadius: "4px",
                "&:hover": {
                  borderColor: colors.primary.main,
                  backgroundColor: "rgba(90, 106, 207, 0.04)",
                  color: colors.primary.main,
                },
                "&.Mui-disabled": {
                  borderColor: "#E5E7EB",
                  color: "#A6ABC8",
                },
              }}>
              Test Send
            </Button>

            {isEditMode && (
              <Tooltip
                title={
                  template.can_delete === false
                    ? "Cannot delete: template is used in active campaigns"
                    : "Delete template"
                }
                arrow>
                <span>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    onClick={handleDeleteOpen}
                    disabled={template.can_delete === false}
                    aria-label="delete template"
                    sx={{
                      borderRadius: "4px",
                      textTransform: "none",
                      fontWeight: 600,
                      borderColor:
                        template.can_delete === false ? "#E5E7EB" : "#FEE2E2",
                      color:
                        template.can_delete === false ? "#A6ABC8" : "#DC2626",
                      "&:hover": {
                        borderColor:
                          template.can_delete === false ? "#E5E7EB" : "#DC2626",
                        backgroundColor:
                          template.can_delete === false
                            ? "transparent"
                            : "rgba(220, 38, 38, 0.04)",
                      },
                      "&.Mui-disabled": {
                        borderColor: "#E5E7EB",
                        color: "#A6ABC8",
                        cursor: "not-allowed",
                      },
                    }}>
                    Delete
                  </Button>
                </span>
              </Tooltip>
            )}

            {fromCampaign && campaignContext ? (
              // Show only Save as Draft button when coming from campaign
              <Tooltip title="Save template and campaign as draft">
                <span>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={() => handleSave("draft")}
                    aria-label="save as draft"
                    sx={{
                      borderRadius: "4px",
                      textTransform: "none",
                      fontWeight: 600,
                      px: 3,
                      backgroundColor: colors.primary.main,
                      "&:hover": {
                        backgroundColor: colors.primary.dark,
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#A6ABC8",
                        color: "#FFFFFF",
                      },
                    }}>
                    Save as Draft
                  </Button>
                </span>
              </Tooltip>
            ) : (
              // Show single save button for normal template editing
              <Tooltip title="Save (Ctrl+S)">
                <span>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={() => handleSave()}
                    aria-label="save template"
                    sx={{
                      borderRadius: "4px",
                      textTransform: "none",
                      fontWeight: 600,
                      px: 3,
                      backgroundColor: colors.primary.main,
                      "&:hover": {
                        backgroundColor: colors.primary.dark,
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#A6ABC8",
                        color: "#FFFFFF",
                      },
                    }}>
                    Save
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, overflow: "hidden", display: "flex" }}>
        {/* Left Panel: Template Details (Bug #5 - max 360px) */}
        <Box
          sx={{
            width: 360,
            maxWidth: 360,
            height: "100%",
            overflow: "auto",
            borderRight: "1px solid #E5E7EB",
            backgroundColor: "#FFFFFF",
            flexShrink: 0,
          }}>
          <Box sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                fontWeight: 700,
                color: "#000000",
                fontFamily: '"Poppins", sans-serif',
              }}>
              Template Details
            </Typography>

            {/* Template Form Fields */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {/* Template Name */}
              <TextField
                fullWidth
                required
                label="Template Name"
                value={template.name}
                onChange={(e) => handleFieldChange("name", e.target.value)}
                error={Boolean(fieldErrors.name)}
                helperText={fieldErrors.name || "Unique name"}
                FormHelperTextProps={{
                  sx: { marginLeft: 0 },
                }}
                placeholder="e.g., Welcome Email"
                size="small"
                inputProps={{
                  "aria-label": "template name",
                  "aria-required": "true",
                }}
              />
              {/* Subject */}
              <TextField
                fullWidth
                required
                label="Subject"
                value={template.subject}
                onChange={(e) => handleFieldChange("subject", e.target.value)}
                placeholder="e.g., Welcome!"
                error={Boolean(fieldErrors.subject)}
                helperText={fieldErrors.subject || "Email subject line"}
                size="small"
                inputProps={{
                  "aria-label": "email subject",
                  "aria-required": "true",
                }}
                FormHelperTextProps={{
                  sx: { marginLeft: 0 },
                }}
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Internal Notes"
                value={template.notes}
                onChange={(e) => handleFieldChange("notes", e.target.value)}
                helperText="Not included in emails"
                placeholder="Add notes..."
                size="small"
                FormHelperTextProps={{
                  sx: { marginLeft: 0 },
                }}
                inputProps={{
                  "aria-label": "internal notes",
                }}
              />
              {/* Audit Information (Edit Mode Only) */}
              {isEditMode && template.created_at && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: colors.text.secondary,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        display: "block",
                        mb: 1,
                      }}>
                      Audit Info
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}>
                      <Typography variant="caption" color="text.secondary">
                        Created:{" "}
                        {new Date(template.created_at).toLocaleDateString()}
                      </Typography>
                      {template.updated_at && (
                        <Typography variant="caption" color="text.secondary">
                          Updated:{" "}
                          {new Date(template.updated_at).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>

        {/* Right Panel: Editor with Tabs (Bug #4) */}
        <Box
          sx={{
            flexGrow: 1,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#FFFFFF",
            overflow: "hidden",
          }}>
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                minHeight: "36px",
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: 100,
                  minHeight: "36px",
                  fontSize: "0.813rem",
                  py: 0.5,
                },
              }}>
              <Tab label="Design" />
              <Tab label="HTML" />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <Box sx={{ flexGrow: 1, overflow: "auto", p: 3 }}>
            {/* Design Tab - Now first (index 0) */}
            {activeTab === 0 && (
              <Box sx={{ width: "100%", height: "100%", borderRadius: "4px" }}>
                {previewLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 400,
                      gap: 2,
                    }}>
                    <CircularProgress />
                    <Typography variant="body2" color="text.secondary">
                      Loading preview...
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {/* Formatting Toolbar - Always visible in Design mode */}
                    <Box sx={{ mb: 2 }}>
                      <Paper
                        elevation={1}
                        sx={{
                          borderRadius: "4px !important",
                          p: 0.75,
                          display: "flex",
                          gap: 0.75,
                          flexWrap: "wrap",
                          alignItems: "center",
                          backgroundColor: "#F9FAFB",
                          border: "1px solid #E5E7EB",
                        }}>
                        <Tooltip title="Bold (Ctrl+B)">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (previewIframeRef.current) {
                                try {
                                  const iframeDoc =
                                    previewIframeRef.current.contentDocument ||
                                    previewIframeRef.current.contentWindow
                                      .document;
                                  iframeDoc.execCommand("bold", false, null);
                                } catch (error) {
                                  console.error("Error executing bold:", error);
                                }
                              }
                            }}
                            sx={{
                              border: "1px solid #E5E7EB",
                              backgroundColor: "#FFFFFF",
                            }}>
                            <FormatBold fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Italic (Ctrl+I)">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (previewIframeRef.current) {
                                try {
                                  const iframeDoc =
                                    previewIframeRef.current.contentDocument ||
                                    previewIframeRef.current.contentWindow
                                      .document;
                                  iframeDoc.execCommand("italic", false, null);
                                } catch (error) {
                                  console.error(
                                    "Error executing italic:",
                                    error,
                                  );
                                }
                              }
                            }}
                            sx={{
                              border: "1px solid #E5E7EB",
                              backgroundColor: "#FFFFFF",
                            }}>
                            <FormatItalic fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Underline (Ctrl+U)">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (previewIframeRef.current) {
                                try {
                                  const iframeDoc =
                                    previewIframeRef.current.contentDocument ||
                                    previewIframeRef.current.contentWindow
                                      .document;
                                  iframeDoc.execCommand(
                                    "underline",
                                    false,
                                    null,
                                  );
                                } catch (error) {
                                  console.error(
                                    "Error executing underline:",
                                    error,
                                  );
                                }
                              }
                            }}
                            sx={{
                              border: "1px solid #E5E7EB",
                              backgroundColor: "#FFFFFF",
                            }}>
                            <FormatUnderlined fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Font Size">
                          <IconButton
                            size="small"
                            onClick={(e) =>
                              setFontSizeMenuAnchor(e.currentTarget)
                            }
                            sx={{
                              border: "1px solid #E5E7EB",
                              backgroundColor: "#FFFFFF",
                            }}>
                            <TextFields fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Divider orientation="vertical" flexItem />

                        <Tooltip title="Align Left">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (previewIframeRef.current) {
                                try {
                                  const iframeDoc =
                                    previewIframeRef.current.contentDocument ||
                                    previewIframeRef.current.contentWindow
                                      .document;
                                  iframeDoc.execCommand(
                                    "justifyLeft",
                                    false,
                                    null,
                                  );
                                } catch (error) {
                                  console.error(
                                    "Error executing justifyLeft:",
                                    error,
                                  );
                                }
                              }
                            }}
                            sx={{
                              border: "1px solid #E5E7EB",
                              backgroundColor: "#FFFFFF",
                            }}>
                            <FormatAlignLeft fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Align Center">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (previewIframeRef.current) {
                                try {
                                  const iframeDoc =
                                    previewIframeRef.current.contentDocument ||
                                    previewIframeRef.current.contentWindow
                                      .document;
                                  iframeDoc.execCommand(
                                    "justifyCenter",
                                    false,
                                    null,
                                  );
                                } catch (error) {
                                  console.error(
                                    "Error executing justifyCenter:",
                                    error,
                                  );
                                }
                              }
                            }}
                            sx={{
                              border: "1px solid #E5E7EB",
                              backgroundColor: "#FFFFFF",
                            }}>
                            <FormatAlignCenter fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Align Right">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (previewIframeRef.current) {
                                try {
                                  const iframeDoc =
                                    previewIframeRef.current.contentDocument ||
                                    previewIframeRef.current.contentWindow
                                      .document;
                                  iframeDoc.execCommand(
                                    "justifyRight",
                                    false,
                                    null,
                                  );
                                } catch (error) {
                                  console.error(
                                    "Error executing justifyRight:",
                                    error,
                                  );
                                }
                              }
                            }}
                            sx={{
                              border: "1px solid #E5E7EB",
                              backgroundColor: "#FFFFFF",
                            }}>
                            <FormatAlignRight fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Divider orientation="vertical" flexItem />

                        {/* Template Alignment Options */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.75rem",
                              color: "#6B7280",
                              fontWeight: 500,
                            }}>
                            Template:
                          </Typography>
                          <Tooltip title="Left-align template layout">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setTemplateAlignment("left");
                              }}
                              sx={{
                                border: "1px solid #E5E7EB",
                                backgroundColor:
                                  templateAlignment === "left"
                                    ? "#E3F2FD"
                                    : "#FFFFFF",
                                color:
                                  templateAlignment === "left"
                                    ? "#1976D2"
                                    : "inherit",
                                "&:hover": {
                                  backgroundColor:
                                    templateAlignment === "left"
                                      ? "#BBDEFB"
                                      : "#F5F5F5",
                                },
                              }}>
                              <AlignHorizontalLeft fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Center-align template layout">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setTemplateAlignment("center");
                              }}
                              sx={{
                                border: "1px solid #E5E7EB",
                                backgroundColor:
                                  templateAlignment === "center"
                                    ? "#E3F2FD"
                                    : "#FFFFFF",
                                color:
                                  templateAlignment === "center"
                                    ? "#1976D2"
                                    : "inherit",
                                "&:hover": {
                                  backgroundColor:
                                    templateAlignment === "center"
                                      ? "#BBDEFB"
                                      : "#F5F5F5",
                                },
                              }}>
                              <AlignHorizontalCenter fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>

                        <Divider orientation="vertical" flexItem />

                        <Tooltip title="Bullet List">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (previewIframeRef.current) {
                                try {
                                  const iframeDoc =
                                    previewIframeRef.current.contentDocument ||
                                    previewIframeRef.current.contentWindow
                                      .document;
                                  iframeDoc.execCommand(
                                    "insertUnorderedList",
                                    false,
                                    null,
                                  );
                                } catch (error) {
                                  console.error(
                                    "Error executing insertUnorderedList:",
                                    error,
                                  );
                                }
                              }
                            }}
                            sx={{
                              border: "1px solid #E5E7EB",
                              backgroundColor: "#FFFFFF",
                            }}>
                            <FormatListBulleted fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Numbered List">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (previewIframeRef.current) {
                                try {
                                  const iframeDoc =
                                    previewIframeRef.current.contentDocument ||
                                    previewIframeRef.current.contentWindow
                                      .document;
                                  iframeDoc.execCommand(
                                    "insertOrderedList",
                                    false,
                                    null,
                                  );
                                } catch (error) {
                                  console.error(
                                    "Error executing insertOrderedList:",
                                    error,
                                  );
                                }
                              }
                            }}
                            sx={{
                              border: "1px solid #E5E7EB",
                              backgroundColor: "#FFFFFF",
                            }}>
                            <FormatListNumbered fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Divider orientation="vertical" flexItem />

                        <Tooltip title="Insert Link">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (previewIframeRef.current) {
                                try {
                                  const iframeDoc =
                                    previewIframeRef.current.contentDocument ||
                                    previewIframeRef.current.contentWindow
                                      .document;
                                  const selection = iframeDoc.getSelection();
                                  const selectedText = selection.toString();
                                  setLinkText(selectedText || "");
                                  setLinkUrl("");
                                  setLinkDialogOpen(true);
                                } catch (error) {
                                  console.error(
                                    "Error opening link dialog:",
                                    error,
                                  );
                                }
                              }
                            }}
                            sx={{
                              border: "1px solid #E5E7EB",
                              backgroundColor: "#FFFFFF",
                            }}>
                            <LinkIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Insert Button">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setButtonUrl("");
                              setButtonText("Click Here");
                              setButtonColor("#28a745");
                              setButtonDialogOpen(true);
                            }}
                            sx={{
                              border: "1px solid #E5E7EB",
                              backgroundColor: "#FFFFFF",
                            }}>
                            <SmartButton fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Insert Placeholder">
                          <IconButton
                            size="small"
                            onClick={(e) =>
                              setPlaceholderMenuAnchor(e.currentTarget)
                            }
                            sx={{
                              border: "1px solid #E5E7EB",
                              backgroundColor: "#FFFFFF",
                            }}>
                            <DataObject fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Divider orientation="vertical" flexItem />

                        <Tooltip title="Text Color">
                          <IconButton
                            size="small"
                            onClick={(e) => setTextColorAnchor(e.currentTarget)}
                            sx={{
                              border: "1px solid #E5E7EB",
                              backgroundColor: "#FFFFFF",
                            }}>
                            <FormatColorText fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Text Background Color">
                          <IconButton
                            size="small"
                            onClick={(e) => setBgColorAnchor(e.currentTarget)}
                            sx={{
                              border: "1px solid #E5E7EB",
                              backgroundColor: "#FFFFFF",
                            }}>
                            <FormatColorText
                              fontSize="small"
                              sx={{ color: "#FFA500" }}
                            />
                          </IconButton>
                        </Tooltip>

                        <Divider orientation="vertical" flexItem />

                        <Tooltip title="Undo (Ctrl+Z)">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (previewIframeRef.current) {
                                try {
                                  const iframeDoc =
                                    previewIframeRef.current.contentDocument ||
                                    previewIframeRef.current.contentWindow
                                      .document;
                                  iframeDoc.execCommand("undo", false, null);
                                } catch (error) {
                                  console.error("Error executing undo:", error);
                                }
                              }
                            }}
                            sx={{
                              border: "1px solid #E5E7EB",
                              backgroundColor: "#FFFFFF",
                            }}>
                            <UndoIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Redo (Ctrl+Y)">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (previewIframeRef.current) {
                                try {
                                  const iframeDoc =
                                    previewIframeRef.current.contentDocument ||
                                    previewIframeRef.current.contentWindow
                                      .document;
                                  iframeDoc.execCommand("redo", false, null);
                                } catch (error) {
                                  console.error("Error executing redo:", error);
                                }
                              }
                            }}
                            sx={{
                              border: "1px solid #E5E7EB",
                              backgroundColor: "#FFFFFF",
                            }}>
                            <RedoIcon />
                          </IconButton>
                        </Tooltip>
                      </Paper>
                    </Box>

                    {template.html_body ? (
                      (() => {
                        // Extract background color and head content from html_body for iframe
                        let iframeBgColor = "transparent";
                        let headContent = "";

                        const bgStyleMatch = template.html_body.match(
                          /background-color:\s*([^;"\s>]+)/i,
                        );
                        if (bgStyleMatch) {
                          iframeBgColor = bgStyleMatch[1];
                        }
                        const bgAttrMatch = template.html_body.match(
                          /bgcolor=["']([^"']+)["']/i,
                        );
                        if (bgAttrMatch) {
                          iframeBgColor = bgAttrMatch[1];
                        }

                        // Extract head content if it's a full HTML document
                        let bodyContent = template.html_body;
                        if (
                          template.html_body
                            .trim()
                            .toLowerCase()
                            .startsWith("<!doctype") ||
                          template.html_body
                            .trim()
                            .toLowerCase()
                            .startsWith("<html")
                        ) {
                          const parser = new DOMParser();
                          const doc = parser.parseFromString(
                            template.html_body,
                            "text/html",
                          );
                          if (doc.head) {
                            let headHtml = doc.head.innerHTML;
                            headHtml = headHtml.replace(
                              /<title[^>]*>.*?<\/title>/gi,
                              "",
                            );
                            headContent = headHtml.trim();
                          }
                          bodyContent = doc.body
                            ? doc.body.innerHTML
                            : template.html_body;
                        }

                        return (
                          <iframe
                            ref={previewIframeRef}
                            title="Email Preview"
                            srcDoc={`<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  ${headContent}
  <style>
    body {
      margin: 0;
      padding: 0;
      font-family: Arial, sans-serif;
      display: flex;
      justify-content: ${templateAlignment === "center" ? "center" : "flex-start"};
      background-color: ${iframeBgColor};
    }
    .email-container {
      max-width: 640px;
      width: 100%;
    }
  </style>
</head>
<body>
  <div class="email-container">
    ${bodyContent}
  </div>
</body>
</html>`}
                            onLoad={() => {
                              // Always enable design mode when iframe loads in Design tab
                              if (previewIframeRef.current) {
                                try {
                                  const iframeDoc =
                                    previewIframeRef.current.contentDocument ||
                                    previewIframeRef.current.contentWindow
                                      .document;
                                  iframeDoc.designMode = "on";
                                  // Apply template alignment to body
                                  if (iframeDoc.body) {
                                    iframeDoc.body.style.justifyContent =
                                      templateAlignment === "center"
                                        ? "center"
                                        : "flex-start";
                                  }
                                } catch (error) {
                                  console.error(
                                    "Error enabling design mode on load:",
                                    error,
                                  );
                                }
                              }
                            }}
                            style={{
                              width: "100%",
                              height: "500px",
                              border: "2px solid #5A6ACF",
                              borderRadius: "4px",
                              backgroundColor: "transparent",
                              transition: "border 0.2s",
                            }}
                          />
                        );
                      })()
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          height: 400,
                          gap: 2,
                        }}>
                        <Typography variant="h6" color="text.secondary">
                          No content yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Add content in the HTML tab to see a preview
                        </Typography>
                      </Box>
                    )}
                  </>
                )}

                {/* Text Color Picker Popover */}
                <Popover
                  open={Boolean(textColorAnchor)}
                  anchorEl={textColorAnchor}
                  onClose={() => setTextColorAnchor(null)}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}>
                  <Box
                    sx={{
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 1 }}>
                      Text Color
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(6, 1fr)",
                        gap: 1,
                      }}>
                      {[
                        { name: "Black", value: "#000000" },
                        { name: "Dark Gray", value: "#4B5563" },
                        { name: "Gray", value: "#9CA3AF" },
                        { name: "Light Gray", value: "#D1D5DB" },
                        { name: "Red", value: "#DC2626" },
                        { name: "Orange", value: "#EA580C" },
                        { name: "Amber", value: "#D97706" },
                        { name: "Yellow", value: "#CA8A04" },
                        { name: "Lime", value: "#65A30D" },
                        { name: "Green", value: "#16A34A" },
                        { name: "Emerald", value: "#059669" },
                        { name: "Teal", value: "#0D9488" },
                        { name: "Cyan", value: "#0891B2" },
                        { name: "Sky", value: "#0284C7" },
                        { name: "Blue", value: "#2563EB" },
                        { name: "Indigo", value: "#4F46E5" },
                        { name: "Violet", value: "#7C3AED" },
                        { name: "Purple", value: "#9333EA" },
                        { name: "Fuchsia", value: "#C026D3" },
                        { name: "Pink", value: "#DB2777" },
                        { name: "Rose", value: "#E11D48" },
                        { name: "White", value: "#FFFFFF" },
                      ].map((color) => (
                        <Tooltip key={color.value} title={color.name}>
                          <Box
                            onClick={() => {
                              if (previewIframeRef.current) {
                                try {
                                  const iframeDoc =
                                    previewIframeRef.current.contentDocument ||
                                    previewIframeRef.current.contentWindow
                                      .document;
                                  iframeDoc.execCommand(
                                    "foreColor",
                                    false,
                                    color.value,
                                  );
                                  setTextColorAnchor(null);
                                } catch (error) {
                                  console.error(
                                    "Error executing foreColor:",
                                    error,
                                  );
                                }
                              }
                            }}
                            sx={{
                              width: 36,
                              height: 36,
                              backgroundColor: color.value,
                              border:
                                color.value === "#FFFFFF"
                                  ? "2px solid #E5E7EB"
                                  : "2px solid transparent",
                              borderRadius: 1,
                              cursor: "pointer",
                              "&:hover": {
                                borderColor: "#4F46E5",
                                transform: "scale(1.1)",
                              },
                              transition: "all 0.2s",
                            }}
                          />
                        </Tooltip>
                      ))}
                    </Box>
                  </Box>
                </Popover>

                {/* Background Color Picker Popover */}
                <Popover
                  open={Boolean(bgColorAnchor)}
                  anchorEl={bgColorAnchor}
                  onClose={() => setBgColorAnchor(null)}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}>
                  <Box
                    sx={{
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 1 }}>
                      Background Color
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(6, 1fr)",
                        gap: 1,
                      }}>
                      {[
                        { name: "None", value: "transparent" },
                        { name: "Black", value: "#000000" },
                        { name: "Dark Gray", value: "#4B5563" },
                        { name: "Gray", value: "#9CA3AF" },
                        { name: "Light Gray", value: "#D1D5DB" },
                        { name: "Red", value: "#FEE2E2" },
                        { name: "Orange", value: "#FFEDD5" },
                        { name: "Amber", value: "#FEF3C7" },
                        { name: "Yellow", value: "#FEF9C3" },
                        { name: "Lime", value: "#ECFCCB" },
                        { name: "Green", value: "#DCFCE7" },
                        { name: "Emerald", value: "#D1FAE5" },
                        { name: "Teal", value: "#CCFBF1" },
                        { name: "Cyan", value: "#CFFAFE" },
                        { name: "Sky", value: "#E0F2FE" },
                        { name: "Blue", value: "#DBEAFE" },
                        { name: "Indigo", value: "#E0E7FF" },
                        { name: "Violet", value: "#EDE9FE" },
                        { name: "Purple", value: "#F3E8FF" },
                        { name: "Fuchsia", value: "#FAE8FF" },
                        { name: "Pink", value: "#FCE7F3" },
                        { name: "Rose", value: "#FFE4E6" },
                      ].map((color) => (
                        <Tooltip key={color.value} title={color.name}>
                          <Box
                            onClick={() => {
                              if (previewIframeRef.current) {
                                try {
                                  const iframeDoc =
                                    previewIframeRef.current.contentDocument ||
                                    previewIframeRef.current.contentWindow
                                      .document;
                                  iframeDoc.execCommand(
                                    "backColor",
                                    false,
                                    color.value,
                                  );
                                  setBgColorAnchor(null);
                                } catch (error) {
                                  console.error(
                                    "Error executing backColor:",
                                    error,
                                  );
                                }
                              }
                            }}
                            sx={{
                              width: 36,
                              height: 36,
                              backgroundColor: color.value,
                              border:
                                color.value === "transparent"
                                  ? "2px dashed #E5E7EB"
                                  : "2px solid #E5E7EB",
                              borderRadius: 1,
                              cursor: "pointer",
                              "&:hover": {
                                borderColor: "#4F46E5",
                                transform: "scale(1.1)",
                              },
                              transition: "all 0.2s",
                            }}
                          />
                        </Tooltip>
                      ))}
                    </Box>
                  </Box>
                </Popover>

                {/* Insert Link Dialog */}
                <Dialog
                  open={linkDialogOpen}
                  onClose={() => {
                    setLinkDialogOpen(false);
                    setLinkUrl("");
                    setLinkText("");
                  }}
                  maxWidth="sm"
                  fullWidth>
                  <DialogTitle
                    sx={{
                      fontSize: "1.25rem",
                      fontWeight: 500,
                      color: colors.text.primary,
                    }}>
                    Insert Link
                  </DialogTitle>
                  <DialogContent>
                    <TextField
                      autoFocus
                      fullWidth
                      label="Link Text"
                      value={linkText}
                      onChange={(e) => setLinkText(e.target.value)}
                      placeholder="Enter text to display"
                      size="small"
                      sx={{ mt: 2, mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="URL"
                      value={linkUrl}
                      onChange={(e) => setLinkUrl(e.target.value)}
                      placeholder="https://example.com"
                      size="small"
                      helperText="Must start with http:// or https://"
                      onKeyPress={(e) => {
                        if (e.key === "Enter" && linkUrl && linkText) {
                          e.preventDefault();
                          if (
                            !linkUrl.startsWith("http://") &&
                            !linkUrl.startsWith("https://")
                          ) {
                            showError(
                              "URL must start with http:// or https://",
                            );
                            return;
                          }

                          if (previewIframeRef.current) {
                            try {
                              const iframeDoc =
                                previewIframeRef.current.contentDocument ||
                                previewIframeRef.current.contentWindow.document;
                              const linkHtml = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
                              iframeDoc.execCommand(
                                "insertHTML",
                                false,
                                linkHtml,
                              );
                              setLinkDialogOpen(false);
                              setLinkUrl("");
                              setLinkText("");
                            } catch (error) {
                              console.error("Error inserting link:", error);
                              showError("Failed to insert link");
                            }
                          }
                        }
                      }}
                    />
                  </DialogContent>
                  <DialogActions sx={{ px: 1, py: 1 }}>
                    <Button
                      onClick={() => {
                        setLinkDialogOpen(false);
                        setLinkUrl("");
                        setLinkText("");
                      }}>
                      Cancel
                    </Button>
                    <Button
                      sx={{ borderRadius: "4px" }}
                      onClick={() => {
                        if (linkUrl && linkText) {
                          if (
                            !linkUrl.startsWith("http://") &&
                            !linkUrl.startsWith("https://")
                          ) {
                            showError(
                              "URL must start with http:// or https://",
                            );
                            return;
                          }

                          if (previewIframeRef.current) {
                            try {
                              const iframeDoc =
                                previewIframeRef.current.contentDocument ||
                                previewIframeRef.current.contentWindow.document;
                              const linkHtml = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
                              iframeDoc.execCommand(
                                "insertHTML",
                                false,
                                linkHtml,
                              );
                              setLinkDialogOpen(false);
                              setLinkUrl("");
                              setLinkText("");
                            } catch (error) {
                              console.error("Error inserting link:", error);
                              showError("Failed to insert link");
                            }
                          }
                        } else {
                          showError("Please enter both link text and URL");
                        }
                      }}
                      variant="contained"
                      disabled={!linkUrl || !linkText}>
                      Insert Link
                    </Button>
                  </DialogActions>
                </Dialog>

                {/* Insert Button Dialog */}
                <Dialog
                  open={buttonDialogOpen}
                  onClose={() => {
                    setButtonDialogOpen(false);
                    setButtonUrl("");
                    setButtonText("Click Here");
                    setButtonColor("#28a745");
                  }}
                  maxWidth="sm"
                  fullWidth>
                  <DialogTitle
                    sx={{
                      fontSize: "1.25rem",
                      fontWeight: 500,
                      color: colors.text.primary,
                    }}>
                    Insert Button
                  </DialogTitle>
                  <DialogContent sx={{ pt: 2 }}>
                    <TextField
                      autoFocus
                      fullWidth
                      label="Button Text"
                      value={buttonText}
                      onChange={(e) => setButtonText(e.target.value)}
                      placeholder="Click Here"
                      size="small"
                      sx={{ mt: 2, mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="Button URL"
                      value={buttonUrl}
                      onChange={(e) => setButtonUrl(e.target.value)}
                      placeholder="https://example.com"
                      size="small"
                      helperText="Must start with http:// or https://"
                      sx={{ mb: 2 }}
                      onKeyPress={(e) => {
                        if (e.key === "Enter" && buttonUrl && buttonText) {
                          e.preventDefault();
                          if (
                            !buttonUrl.startsWith("http://") &&
                            !buttonUrl.startsWith("https://")
                          ) {
                            showError(
                              "URL must start with http:// or https://",
                            );
                            return;
                          }

                          if (previewIframeRef.current) {
                            try {
                              const iframeDoc =
                                previewIframeRef.current.contentDocument ||
                                previewIframeRef.current.contentWindow.document;
                              const buttonHtml = `<div style="text-align: center; margin: 20px 0;"><a href="${buttonUrl}" target="_blank" rel="noopener noreferrer" style="display: inline-block; padding: 12px 24px; background-color: ${buttonColor}; color: #ffffff; text-decoration: none; border-radius: 4px; font-weight: 500;">${buttonText}</a></div>`;
                              iframeDoc.execCommand(
                                "insertHTML",
                                false,
                                buttonHtml,
                              );
                              setButtonDialogOpen(false);
                              setButtonUrl("");
                              setButtonText("Click Here");
                              setButtonColor("#28a745");
                            } catch (error) {
                              console.error("Error inserting button:", error);
                              showError("Failed to insert button");
                            }
                          }
                        }
                      }}
                    />

                    {/* Button Color Picker */}
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: colors.text.primary,
                        }}>
                        Button Color
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {[
                          { name: "Green", value: "#28a745" },
                          { name: "Blue", value: "#2563EB" },
                          { name: "Red", value: "#DC2626" },
                          { name: "Orange", value: "#EA580C" },
                          { name: "Purple", value: "#9333EA" },
                          { name: "Teal", value: "#0D9488" },
                          { name: "Indigo", value: "#4F46E5" },
                          { name: "Pink", value: "#DB2777" },
                          { name: "Gray", value: "#4B5563" },
                          { name: "Black", value: "#000000" },
                        ].map((color) => (
                          <Tooltip key={color.value} title={color.name}>
                            <Box
                              onClick={() => setButtonColor(color.value)}
                              sx={{
                                width: 40,
                                height: 40,
                                backgroundColor: color.value,
                                border:
                                  buttonColor === color.value
                                    ? "3px solid #4F46E5"
                                    : "2px solid #E5E7EB",
                                borderRadius: 1,
                                cursor: "pointer",
                                "&:hover": {
                                  borderColor: "#4F46E5",
                                  transform: "scale(1.05)",
                                },
                                transition: "all 0.2s",
                              }}
                            />
                          </Tooltip>
                        ))}
                      </Box>
                    </Box>
                  </DialogContent>
                  <DialogActions sx={{ px: 1, py: 1 }}>
                    <Button
                      onClick={() => {
                        setButtonDialogOpen(false);
                        setButtonUrl("");
                        setButtonText("Click Here");
                        setButtonColor("#28a745");
                      }}>
                      Cancel
                    </Button>
                    <Button
                      sx={{ borderRadius: "4px" }}
                      onClick={() => {
                        if (buttonUrl && buttonText) {
                          if (
                            !buttonUrl.startsWith("http://") &&
                            !buttonUrl.startsWith("https://")
                          ) {
                            showError(
                              "URL must start with http:// or https://",
                            );
                            return;
                          }

                          if (previewIframeRef.current) {
                            try {
                              const iframeDoc =
                                previewIframeRef.current.contentDocument ||
                                previewIframeRef.current.contentWindow.document;
                              const buttonHtml = `<div style="text-align: center; margin: 20px 0;"><a href="${buttonUrl}" target="_blank" rel="noopener noreferrer" style="display: inline-block; padding: 12px 24px; background-color: ${buttonColor}; color: #ffffff; text-decoration: none; border-radius: 4px; font-weight: 500;">${buttonText}</a></div>`;
                              iframeDoc.execCommand(
                                "insertHTML",
                                false,
                                buttonHtml,
                              );
                              setButtonDialogOpen(false);
                              setButtonUrl("");
                              setButtonText("Click Here");
                              setButtonColor("#28a745");
                            } catch (error) {
                              console.error("Error inserting button:", error);
                              showError("Failed to insert button");
                            }
                          }
                        } else {
                          showError("Please enter both button text and URL");
                        }
                      }}
                      variant="contained"
                      disabled={!buttonUrl || !buttonText}>
                      Insert Button
                    </Button>
                  </DialogActions>
                </Dialog>

                {/* Font Size Menu */}
                <Menu
                  anchorEl={fontSizeMenuAnchor}
                  open={Boolean(fontSizeMenuAnchor)}
                  onClose={() => setFontSizeMenuAnchor(null)}>
                  <MenuItem
                    disabled
                    sx={{ fontWeight: 600, color: colors.text.primary }}>
                    Font Size
                  </MenuItem>
                  {[
                    { label: "12px", value: "1" },
                    { label: "14px", value: "2" },
                    { label: "16px", value: "3" },
                    { label: "18px", value: "4" },
                    { label: "20px", value: "5" },
                    { label: "24px", value: "6" },
                    { label: "28px", value: "7" },
                    { label: "32px", value: "7" },
                  ].map((size, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => {
                        if (previewIframeRef.current) {
                          try {
                            const iframeDoc =
                              previewIframeRef.current.contentDocument ||
                              previewIframeRef.current.contentWindow.document;
                            iframeDoc.execCommand(
                              "fontSize",
                              false,
                              size.value,
                            );
                            setFontSizeMenuAnchor(null);
                          } catch (error) {
                            console.error("Error setting font size:", error);
                            showError("Failed to set font size");
                          }
                        }
                      }}
                      sx={{ fontSize: "0.875rem" }}>
                      {size.label}
                    </MenuItem>
                  ))}
                </Menu>

                {/* Placeholder Menu */}
                <Menu
                  anchorEl={placeholderMenuAnchor}
                  open={Boolean(placeholderMenuAnchor)}
                  onClose={() => setPlaceholderMenuAnchor(null)}>
                  <MenuItem
                    disabled
                    sx={{ fontWeight: 600, color: colors.text.primary }}>
                    Placeholders
                  </MenuItem>
                  {availablePlaceholders.map((placeholder, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => {
                        if (previewIframeRef.current) {
                          try {
                            const iframeDoc =
                              previewIframeRef.current.contentDocument ||
                              previewIframeRef.current.contentWindow.document;
                            iframeDoc.execCommand(
                              "insertHTML",
                              false,
                              placeholder.token,
                            );
                            setPlaceholderMenuAnchor(null);
                          } catch (error) {
                            console.error(
                              "Error inserting placeholder:",
                              error,
                            );
                            showError("Failed to insert placeholder");
                          }
                        }
                      }}
                      sx={{ fontSize: "0.875rem" }}>
                      {placeholder.label}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            )}

            {/* HTML Tab - Now second (index 1) */}
            {activeTab === 1 && (
              <TextField
                multiline
                fullWidth
                value={template.html_body}
                onChange={(e) => handleFieldChange("html_body", e.target.value)}
                variant="outlined"
                minRows={20}
                sx={{
                  borderRadius: "4px",
                  maxHeight: "500px",
                  fontFamily: "monospace",
                  fontSize: "0.875rem",
                  "& .MuiInputBase-root": {
                    fontFamily: "monospace",
                  },
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Test Send Dialog */}
      <Dialog
        open={testSendDialogOpen}
        onClose={handleTestSendClose}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>Send Test Email</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Send a test email with dummy placeholder data to verify the template
            rendering.
          </Typography>
          <Alert severity="info" sx={{ mb: 2, fontSize: "0.875rem" }}>
            The email will be sent with sample data including: John Doe, Sample
            Organization, ₹1000 donation, etc.
          </Alert>
          <TextField
            autoFocus
            fullWidth
            label="Recipient Email"
            type="email"
            value={testSendEmail}
            onChange={(e) => setTestSendEmail(e.target.value)}
            placeholder="test@example.com"
            helperText="Enter the email address to receive the test"
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleTestSend();
              }
            }}
            inputProps={{
              "aria-label": "test recipient email",
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTestSendClose} disabled={testSending}>
            Cancel
          </Button>
          <Button
            sx={{ borderRadius: "4px" }}
            onClick={handleTestSend}
            variant="contained"
            disabled={testSending}
            startIcon={testSending ? <CircularProgress size={20} /> : <Send />}>
            {testSending ? "Sending..." : "Send Test"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteClose}
        onConfirm={handleDelete}
        title="Delete Template"
        itemName="template"
        deleting={deleting}>
        <Typography
          variant="body2"
          sx={{ color: colors.text.secondary, px: 2 }}>
          Are you sure you want to delete the template "{template.name}"?
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: colors.text.secondary, px: 2, mt: 1 }}>
          This action will soft-delete the template. It can be recovered by an
          administrator.
        </Typography>
      </DeleteConfirmationDialog>
    </Box>
  );
};

export default EmailTemplateEditor;
